import { useQuery } from '@tanstack/react-query'

import { api } from '@/api/client'
import type { components } from '@/api/types.gen'

export type PartListRow = components['schemas']['PartListRow']
export type PartsPage = components['schemas']['PartsPage']
export type PartDetail = components['schemas']['PartDetail']
export type Part = components['schemas']['Part']
export type PartLot = components['schemas']['PartLot']
export type StockMovement = components['schemas']['StockMovement']
export type PartParameter = components['schemas']['PartParameter']
export type TagCount = components['schemas']['TagCount']

/** Everything the parts list can be narrowed by. Mirrors the URL. */
export interface PartsFilter {
  q?: string
  category_id?: string
  include_subcategories?: boolean
  footprint_id?: string
  manufacturer_id?: string
  supplier_id?: string
  location_id?: string
  tags?: string[]
  in_stock?: boolean
  low_stock?: boolean
  favorite?: boolean
  needs_review?: boolean
  status?: string
  sort?: string
  order?: 'asc' | 'desc'
  page?: number
  per_page?: number
}

export function partsKey(filter: PartsFilter) {
  return ['parts', filter] as const
}

export async function fetchParts(filter: PartsFilter): Promise<PartsPage> {
  const { data } = await api.GET('/api/parts', {
    params: {
      query: {
        ...filter,
        tags: filter.tags?.length ? filter.tags.join(',') : undefined,
      },
    },
  })
  return data as PartsPage
}

export function useParts(filter: PartsFilter) {
  return useQuery({
    queryKey: partsKey(filter),
    queryFn: () => fetchParts(filter),
    placeholderData: (previous) => previous,
  })
}

export function partKey(id: string) {
  return ['part', id] as const
}

export async function fetchPart(id: string): Promise<PartDetail> {
  const { data } = await api.GET('/api/parts/{id}', { params: { path: { id } } })
  return data as PartDetail
}

export function usePart(id: string) {
  return useQuery({ queryKey: partKey(id), queryFn: () => fetchPart(id) })
}

/**
 * Mark a part as a favourite or not. The API has a dedicated route for the
 * flag, so the rest of the part is not read or rewritten.
 */
export async function setFavorite(id: string, favorite: boolean): Promise<void> {
  const { error } = await api.PUT('/api/parts/{id}/favorite', {
    params: { path: { id } },
    body: { favorite },
  })
  if (error) throw new Error('Could not save the favourite')
}

export type ProjectUse = components['schemas']['ProjectUse']

/** The projects whose bill of material names a part. */
export function usePartProjects(id: string) {
  return useQuery({
    queryKey: ['part-projects', id] as const,
    queryFn: async (): Promise<ProjectUse[]> => {
      const { data } = await api.GET('/api/parts/{id}/projects', { params: { path: { id } } })
      return data ?? []
    },
  })
}

export const tagsKey = ['tags'] as const

export async function fetchTags(): Promise<TagCount[]> {
  const { data } = await api.GET('/api/tags')
  return data ?? []
}

export function useTags() {
  return useQuery({ queryKey: tagsKey, queryFn: fetchTags })
}

export function movementsKey(partId: string) {
  return ['part', partId, 'movements'] as const
}

export async function fetchMovements(partId: string): Promise<StockMovement[]> {
  const { data } = await api.GET('/api/parts/{id}/movements', {
    params: { path: { id: partId } },
  })
  return data ?? []
}

/** How a manufacturing status reads in the interface. */
export const STATUS_LABELS: Record<string, string> = {
  unknown: 'Unknown',
  announced: 'Announced',
  active: 'Active',
  nrfnd: 'Not recommended for new designs',
  eol: 'End of life',
  discontinued: 'Discontinued',
}
